import * as React from "react";
import ReactSelect, { components as Components, createFilter } from "react-select";
import isEqual from "lodash/isEqual";
import { matchSorter } from "match-sorter";
import { withTheme } from "styled-components";
import CreatableSelect from "react-select/creatable";
import CustomMenuList from "components/common/Select/CustomMenuList";
import Icon from "components/common/Icon";
import { INPUT_BORDER_RADIUS } from "theme/constants";
import AsyncCustomMenuList from "./AsyncCustomMenuList";
export const CONTROL_CLASS = "common-select-control";
const MultiValueRemove = ({
  children = void 0,
  ...props
}) => /* @__PURE__ */ React.createElement(Components.MultiValueRemove, { ...props }, children);
const IndicatorSeparator = () => null;
const DropdownIndicator = (props) => {
  const {
    children = /* @__PURE__ */ React.createElement(Icon, { name: "arrow_drop_down" }),
    getStyles,
    innerProps: { ref, ...restInnerProps }
  } = props;
  return /* @__PURE__ */ React.createElement("div", { style: getStyles("dropdownIndicator", props), ref, ...restInnerProps }, children);
};
const Control = ({ children = void 0, ...props }) => /* @__PURE__ */ React.createElement(Components.Control, { ...props, className: CONTROL_CLASS }, children);
const CustomOption = (optionRenderer) => ({ data, isSelected, ...props }) => /* @__PURE__ */ React.createElement(Components.Option, { data, isSelected, ...props }, optionRenderer(data, isSelected));
const CustomSingleValue = (valueRenderer) => ({ data, ...props }) => /* @__PURE__ */ React.createElement(Components.SingleValue, { data, ...props }, valueRenderer(data));
const CustomMultiValueLabel = (valueRenderer) => ({ data, ...props }) => /* @__PURE__ */ React.createElement(Components.MultiValueLabel, { data, ...props }, valueRenderer(data));
const CustomInput = (inputProps) => (props) => /* @__PURE__ */ React.createElement(Components.Input, { ...props, ...inputProps });
const dropdownIndicator = (base, state) => ({
  ...base,
  padding: "0px",
  fontSize: "150%",
  marginRight: "1rem",
  transform: state.selectProps.menuIsOpen && "rotate(180deg)"
});
const clearIndicator = (base) => ({
  ...base,
  padding: "5px"
});
const multiValue = ({ theme }) => (base, state) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  return {
    ...base,
    border: `1px solid ${((_c = (_b = state.option) == null ? void 0 : _b[(_a = state.data) == null ? void 0 : _a.value]) == null ? void 0 : _c.isFixed) ? theme.colors.variant.lightest.default : theme.colors.variant.lighter.info}`,
    background: ((_f = (_e = state.option) == null ? void 0 : _e[(_d = state.data) == null ? void 0 : _d.value]) == null ? void 0 : _f.isFixed) ? theme.colors.input.background : theme.colors.variant.lightest.info,
    color: ((_i = (_h = state.option) == null ? void 0 : _h[(_g = state.data) == null ? void 0 : _g.value]) == null ? void 0 : _i.isFixed) ? theme.colors.input.colorDisabled : theme.colors.text.secondary
  };
};
const multiValueLabel = ({ theme }) => (base, state) => {
  var _a;
  return {
    ...base,
    padding: "2px 5px",
    fontSize: theme.fonts.size.small,
    color: ((_a = state.data) == null ? void 0 : _a.isFixed) ? theme.colors.text.secondary : theme.colors.text.primary
  };
};
const multiValueRemove = ({ theme }) => (base) => ({
  ...base,
  borderLeft: `1px solid ${theme.colors.variant.lighter.info}`,
  paddingLeft: "5px",
  paddingRight: "5px",
  borderRadius: "0"
});
const controlSmall = {
  minHeight: "29px",
  height: "29px"
};
const controlNormal = {
  minHeight: "34px"
};
const menu = (base) => ({
  ...base,
  zIndex: 5
});
const menuPortal = (base) => ({
  ...base,
  zIndex: 1061
});
const singleValueAndPlaceholder = ({ theme }) => (base) => ({
  ...base,
  lineHeight: "28px",
  fontFamily: theme.fonts.family.body,
  fontSize: theme.fonts.size.body,
  fontWeight: 400
});
const placeholderStyling = ({ theme }) => (base) => ({
  ...base,
  color: theme.colors.input.placeholder,
  lineHeight: "28px",
  fontFamily: theme.fonts.family.body,
  fontSize: theme.fonts.size.body,
  fontWeight: 400,
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  maxWidth: "100%",
  paddingRight: "20px"
});
const controlFocus = ({ size, theme }) => (base, { isFocused }) => {
  const borderWidth = isFocused ? 1 : base.borderWidth;
  const outline = isFocused ? 0 : base.outline;
  const boxShadow = isFocused ? theme.colors.input.boxShadow : null;
  const controlSize = size === "small" ? controlSmall : controlNormal;
  return {
    ...base,
    ...controlSize,
    borderWidth,
    boxShadow,
    outline,
    borderRadius: INPUT_BORDER_RADIUS,
    alignItems: "center"
  };
};
const valueContainer = ({ size }) => (base) => ({
  ...base,
  padding: size === "small" ? "0 8px" : "2px 10px"
});
const _components = {
  DropdownIndicator,
  MultiValueRemove,
  IndicatorSeparator,
  Control
};
const _styles = ({ size, theme }) => ({
  dropdownIndicator,
  clearIndicator,
  multiValue: multiValue({ theme }),
  multiValueLabel: multiValueLabel({ theme }),
  multiValueRemove: multiValueRemove({ theme }),
  menu,
  menuPortal,
  singleValue: singleValueAndPlaceholder({ theme }),
  placeholder: placeholderStyling({ theme }),
  control: controlFocus({ size, theme }),
  valueContainer: valueContainer({ size })
});
export const matchOptions = (options, query, { displayKey = "label", ignoreAccents = true } = {}) => matchSorter(options, query, { keys: [displayKey, "label"], keepDiacritics: !ignoreAccents });
const getCustomComponents = (inputProps, optionRenderer, valueRenderer, async) => {
  const customComponents = {};
  if (inputProps) {
    customComponents.Input = CustomInput(inputProps);
  }
  if (optionRenderer) {
    customComponents.Option = CustomOption(optionRenderer);
  }
  if (valueRenderer) {
    customComponents.SingleValue = CustomSingleValue(valueRenderer);
    customComponents.MultiValueLabel = CustomMultiValueLabel(valueRenderer);
  }
  customComponents.MenuList = async ? AsyncCustomMenuList : CustomMenuList;
  return customComponents;
};
class Select extends React.Component {
  constructor(props) {
    super(props);
    // eslint-disable-next-line react/no-unused-class-component-methods
    this.getValue = () => {
      const { value } = this.state;
      return value;
    };
    // eslint-disable-next-line react/no-unused-class-component-methods
    this.clearValue = () => {
      this.setState({ value: void 0 });
    };
    this._extractOptionValue = (onChangeValue) => {
      const { multi, valueKey, delimiter } = this.props;
      if (onChangeValue) {
        return multi && Array.isArray(onChangeValue) ? onChangeValue.map((i) => i[valueKey]).join(delimiter) : onChangeValue[valueKey || ""];
      }
      return "";
    };
    this._onChange = (selectedOption, actionMeta) => {
      let value = this._extractOptionValue(selectedOption);
      if (["remove-value", "pop-value"].includes(actionMeta.action)) {
        const removed = actionMeta.removedValue;
        if (removed == null ? void 0 : removed.isFixed) {
          const fixed = (this.state.value || []).filter((o) => o.isFixed);
          value = [...fixed, ...selectedOption.filter((o) => !o.isFixed)];
        }
      }
      if (this.props.persistSelection) {
        this.setState({ value });
      }
      const { onChange = () => {
      } } = this.props;
      onChange(value, actionMeta);
    };
    // Using ReactSelect.Creatable now needs to get values as objects or they are not display
    // This method takes care of formatting a string value into options react-select supports.
    this._formatInputValue = (value) => {
      const { options, displayKey, valueKey, delimiter, allowCreate, async } = this.props;
      if (value === void 0 || value === null || typeof value === "string" && value === "") {
        return [];
      }
      if (Array.isArray(value)) {
        return value;
      }
      if ((allowCreate || async) && typeof value === "string") {
        return value.split(delimiter).map((optionValue) => {
          const predicate = {
            [valueKey]: optionValue,
            [displayKey]: optionValue
          };
          const option = options.find((o) => o[valueKey] === optionValue);
          return option || predicate;
        });
      }
      return (typeof value === "string" ? (value != null ? value : "").split(delimiter) : [value]).map((v) => {
        const availableOption = options.find((option) => option[valueKey || ""] === v);
        return availableOption != null ? availableOption : { [displayKey]: String(value), [valueKey]: value };
      });
    };
    this._selectTheme = (defaultTheme) => {
      const { theme } = this.props;
      return {
        ...defaultTheme,
        colors: {
          ...defaultTheme.colors,
          primary: theme.colors.input.borderFocus,
          primary75: theme.colors.variant.light.default,
          primary50: theme.colors.variant.lighter.default,
          primary25: theme.colors.variant.lightest.default,
          danger: theme.colors.variant.darker.info,
          dangerLight: theme.colors.variant.lighter.info,
          neutral0: theme.colors.input.background,
          neutral5: theme.colors.input.backgroundDisabled,
          neutral10: theme.colors.variant.lightest.info,
          neutral20: theme.colors.input.border,
          neutral30: theme.colors.gray[70],
          neutral40: theme.colors.gray[60],
          neutral50: theme.colors.gray[50],
          neutral60: theme.colors.gray[40],
          neutral70: theme.colors.gray[30],
          neutral80: theme.colors.gray[20],
          neutral90: theme.colors.gray[10]
        }
      };
    };
    this.createCustomFilter = () => {
      const { ignoreAccents } = this.props;
      return createFilter({
        ignoreAccents,
        stringify: (option) => String(option.label)
      });
    };
    this._onInputChange = (newValue, actionMeta) => {
      this.setState({ inputValue: newValue });
      const { onInputChange } = this.props;
      if (onInputChange) onInputChange(newValue, actionMeta);
    };
    const { inputProps, optionRenderer, value, valueRenderer, async } = props;
    this.state = {
      customComponents: getCustomComponents(inputProps, optionRenderer, valueRenderer, async),
      value,
      inputValue: ""
    };
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { inputProps, optionRenderer, value, valueRenderer, async } = this.props;
    if (value !== nextProps.value) {
      this.setState({ value: nextProps.value });
    }
    if (!isEqual(inputProps, nextProps.inputProps) || optionRenderer !== nextProps.optionRenderer || valueRenderer !== nextProps.valueRenderer) {
      this.setState({
        customComponents: getCustomComponents(inputProps, optionRenderer, valueRenderer, async)
      });
    }
  }
  render() {
    var _a;
    const {
      allowCreate = false,
      displayKey,
      components,
      valueKey,
      onReactSelectChange,
      size,
      theme,
      ignoreAccents
    } = this.props;
    const { customComponents, value, inputValue: uncontrolledInputValue } = this.state;
    const inputValue = (_a = this.props.inputValue) != null ? _a : uncontrolledInputValue;
    const formattedValue = this._formatInputValue(value);
    const {
      multi: isMulti,
      disabled: isDisabled,
      clearable: isClearable,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      inputProps,
      // Do not pass down prop
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      optionRenderer,
      // Do not pass down prop
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      valueRenderer,
      // Do not pass down prop
      async,
      total,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onInputChange,
      // Do not pass down prop — routed through _onInputChange
      loadOptions,
      "aria-label": ariaLabel,
      placeholder,
      styles,
      options: rawOptions,
      ...rest
    } = this.props;
    const customFilter = this.createCustomFilter();
    const sortedOptions = !async && inputValue ? matchOptions(rawOptions, inputValue, { displayKey, ignoreAccents }) : rawOptions;
    const mergedComponents = {
      ..._components,
      ...components,
      ...customComponents
    };
    const selectProps = {
      ...rest,
      options: sortedOptions,
      onChange: onReactSelectChange || this._onChange,
      onInputChange: this._onInputChange,
      inputValue,
      "aria-label": ariaLabel != null ? ariaLabel : placeholder,
      placeholder,
      async,
      isMulti,
      isDisabled,
      isClearable,
      loadOptions,
      getOptionLabel: (option) => option[displayKey] || option.label,
      getOptionValue: (option) => {
        const v = option[valueKey];
        return typeof v === "object" && v !== null ? JSON.stringify(v) : String(v != null ? v : "");
      },
      filterOption: async ? customFilter : null,
      components: mergedComponents,
      menuPortalTarget: document.body,
      isOptionDisabled: (option) => !!option.disabled,
      styles: { ..._styles({ size, theme }), ...styles },
      theme: this._selectTheme,
      total,
      value: formattedValue
    };
    if (allowCreate) {
      return /* @__PURE__ */ React.createElement(CreatableSelect, { ref: rest.forwardedRef, ...selectProps });
    }
    return /* @__PURE__ */ React.createElement(ReactSelect, { ref: rest.forwardedRef, ...selectProps });
  }
}
Select.defaultProps = {
  "aria-label": void 0,
  addLabelText: void 0,
  allowCreate: false,
  autoFocus: false,
  className: void 0,
  clearable: true,
  components: null,
  delimiter: ",",
  disabled: false,
  displayKey: "label",
  persistSelection: true,
  id: void 0,
  ignoreAccents: true,
  inputId: void 0,
  onBlur: void 0,
  inputProps: void 0,
  isLoading: void 0,
  multi: false,
  menuIsOpen: void 0,
  name: void 0,
  openMenuOnFocus: void 0,
  onReactSelectChange: void 0,
  onMenuClose: void 0,
  optionRenderer: void 0,
  placeholder: void 0,
  required: false,
  size: "normal",
  styles: void 0,
  value: void 0,
  valueKey: "value",
  valueRenderer: void 0,
  menuPlacement: "auto",
  maxMenuHeight: 300,
  async: false,
  total: 0,
  onInputChange: void 0,
  inputValue: void 0,
  loadOptions: void 0,
  forwardedRef: void 0
};
export default withTheme(Select);
